/**
 * OCEAN collections outside the search page.
 *
 * OceanSavedTab — Profile → Saved → OCEAN (the Saved tab's OCEAN section, for
 * accounts OCEAN lets in; "View in Profile" and the rail's collections in
 * OCEAN open it). It loads the member's collections and saved cards itself:
 *   - the collections, each with its card count (and "Shared" while shared);
 *     a new one by name;
 *   - a collection opened: its cards (problem / company, filterable), each
 *     opening back in OCEAN, with Move (to another collection) and Remove;
 *   - a collection's menu: Rename, Share (the Cosmos share menu: Copy link,
 *     WhatsApp, LinkedIn — a tracked link, kind ocean_collection; held back
 *     while COLLECTION_SHARING_ENABLED is false), Stop sharing, Delete (its
 *     cards move to the default collection, as the backend does; the default
 *     itself is never deleted).
 * A card that has since left the index keeps its saved title and says so.
 *
 * SharedCollectionView — a collection someone shared, opened from its link
 * inside OCEAN (/?view=ocean&ocean=collection:<token>): read-only, its name,
 * the owner's first name and its cards; a card opens as any card does and
 * saves into the viewer's own collections. A revoked or deleted collection
 * says "This collection is no longer available."
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Icon } from './icons';
import { oc, rememberCollection } from './api';
import type { Collection, Kind, SavedItem, SharedCollection } from './api';
import { useOcean } from './context';
import { COLLECTION_SHARING_ENABLED, clearSavedFocus, peekSavedFocus } from './flags';
import { CollectionSheet, ConfirmSheet, Toast } from './Sheets';
import type { ToastState } from './Sheets';
import { useOceanStyles } from './styles';
import { useCollectionStyles } from './collectionStyles';
import { ocTrack } from './track';
import { SaveIcon, Skeleton, fmt } from './ui';
import { ShareMenu } from '../techcard/ShareMenu';
import { useTechCardStyles } from '../techcard/styles';

type Family = 'all' | 'problem' | 'company';

const BADGE: Record<Kind, string> = { problem: 'Ocean problem card', company: 'Ocean company card', solution: 'COSMOS technology' };
const cards = (n: number) => `${fmt(n)} card${n === 1 ? '' : 's'}`;
const shareTrack = (action: string, props: Record<string, unknown>) =>
  ocTrack(action.replace(/^techcard_/, 'ocean_collection_'), props);
/** What a shared collection is called where the link lands (WhatsApp puts it before the URL). */
const shareTitle = (name: string) => `${name} · OCEAN collection`;

// ---------------------------------------------------------------- share button
/** Share: the collection's link is made on the first click (its token), then
 *  the Cosmos share menu opens on it; once shared, the button is the menu. */
const ShareCollection: React.FC<{
  c: Collection; autoStart?: boolean; onShared: (token: string) => void; toast: (m: string) => void;
}> = ({ c, autoStart, onShared, toast }) => {
  const [busy, setBusy] = useState(false);
  const [justShared, setJustShared] = useState(false);
  const start = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    const r = await oc.shareCollection(c.id);
    setBusy(false);
    if (!r.ok || !r.data) { toast(r.error || 'Could not share this collection.'); return; }
    ocTrack('ocean_collection_share_link', { created: r.data.share.created, cards: c.count });
    setJustShared(true);
    onShared(r.data.share.token);
  }, [busy, c.id, c.count, onShared, toast]);
  const started = useRef(false);
  useEffect(() => {
    if (autoStart && !started.current && !c.share_token) { started.current = true; start(); }
  }, [autoStart, c.share_token, start]);
  const content = <><Icon name="share" size={16} /><span>Share</span></>;
  if (c.share_token) {
    return (
      <ShareMenu key={c.share_token} target={{ kind: 'ocean_collection', id: c.share_token }} title={shareTitle(c.name)}
                 surface="ocean_collection" onToast={toast} track={shareTrack} label={`Share the collection “${c.name}”`}
                 buttonClassName="oc-act" buttonContent={content} defaultOpen={justShared || !!autoStart} />
    );
  }
  return (
    <button type="button" className="oc-act" onClick={start} disabled={busy} aria-label={`Share the collection “${c.name}”`}>
      {content}{busy ? '…' : ''}
    </button>
  );
};

// ---------------------------------------------------------------- the "…" menu
interface MenuActions { open?: () => void; rename: () => void; share: () => void; unshare: () => void; remove: () => void }

const CollectionMenu: React.FC<{ c: Collection; a: MenuActions; className?: string }> = ({ c, a, className }) => {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => { if (!wrap.current?.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); } };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey, true);
    wrap.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey, true); };
  }, [open]);
  const item = (label: string, run: () => void, danger = false) => (
    <button type="button" role="menuitem" className={danger ? 'danger' : undefined}
            onClick={e => { e.stopPropagation(); setOpen(false); run(); }}>{label}</button>
  );
  return (
    <div className={`occ-menu-wrap ${className || ''}`} ref={wrap}>
      <button type="button" className="oc-ico" aria-haspopup="menu" aria-expanded={open} title="Collection options"
              aria-label={`Options for “${c.name}”`} onClick={e => { e.stopPropagation(); setOpen(o => !o); }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <circle cx="5" cy="12" r="1.8" /><circle cx="12" cy="12" r="1.8" /><circle cx="19" cy="12" r="1.8" />
        </svg>
      </button>
      {open && (
        <div className="occ-menu" role="menu" aria-label={`Options for “${c.name}”`}>
          {a.open && item('Open', a.open)}
          {item('Rename', a.rename)}
          {COLLECTION_SHARING_ENABLED && item(c.share_token ? 'Share link' : 'Share', a.share)}
          {c.share_token && item('Stop sharing', a.unshare)}
          {!c.is_default && item('Delete', a.remove, true)}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------- Profile → Saved → OCEAN
export const OceanSavedTab: React.FC<{
  onOpen: (kind: 'problem' | 'company', id: string) => void;
  onOpenTech: (id: string) => void;
}> = ({ onOpen, onOpenTech }) => {
  useOceanStyles();
  useCollectionStyles();
  useTechCardStyles();   // the Cosmos share menu
  const [items, setItems] = useState<SavedItem[] | null>(null);
  const [collections, setCollections] = useState<Collection[]>([]);
  const [error, setError] = useState('');
  const [family, setFamily] = useState<Family>('all');
  const [openId, setOpenId] = useState<number | null>(() => peekSavedFocus()?.id ?? null);
  useEffect(() => { clearSavedFocus(); }, []);
  const [moving, setMoving] = useState<SavedItem | null>(null);
  const [renaming, setRenaming] = useState<{ id: number; name: string; err: string } | null>(null);
  const [deleting, setDeleting] = useState<Collection | null>(null);
  const [shareNow, setShareNow] = useState<number | null>(null);
  const [creating, setCreating] = useState<{ name: string; err: string; busy: boolean } | null>(null);
  const [toast, setToast] = useState<ToastState | null>(null);
  const dismiss = useCallback(() => setToast(null), []);
  const flash = useCallback((msg: string) => setToast({ msg }), []);

  const load = useCallback(async () => {
    const r = await oc.saved();
    if (r.ok && r.data) { setItems(r.data.items); setCollections(r.data.collections); setError(''); }
    else { setItems(i => i ?? []); setError(r.error || 'Could not load your saved cards.'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const all = items || [];
  const current = openId != null ? collections.find(c => c.id === openId) || null : null;
  // A collection that went away (deleted elsewhere): back to the list.
  useEffect(() => { if (items && openId != null && collections.length && !current) setOpenId(null); }, [items, openId, collections, current]);
  useEffect(() => { setFamily('all'); setRenaming(null); }, [openId]);
  const dfltName = collections.find(c => c.is_default)?.name || 'Saved';

  const patchCollection = (id: number, patch: Partial<Collection>) =>
    setCollections(cs => cs.map(c => (c.id === id ? { ...c, ...patch } : c)));

  // ---------------------------------------------------------------- collection actions
  const openCollection = (c: Collection) => { setOpenId(c.id); ocTrack('ocean_collection_open', { from: 'profile', cards: c.count }); };
  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!creating || !creating.name.trim() || creating.busy) return;
    setCreating({ ...creating, busy: true });
    const r = await oc.createCollection(creating.name.trim());
    if (!r.ok || !r.data) { setCreating({ ...creating, busy: false, err: r.error }); return; }
    ocTrack('ocean_collection_create', { from: 'profile' });
    setCreating(null);
    flash(`Collection “${r.data.collection.name}” created.`);
    await load();
  };
  const rename = async () => {
    if (!renaming) return;
    const n = renaming.name.trim();
    const was = collections.find(c => c.id === renaming.id);
    if (!n || n === was?.name) { setRenaming(null); return; }
    const r = await oc.renameCollection(renaming.id, n);
    if (!r.ok) { setRenaming({ ...renaming, err: r.error }); return; }
    ocTrack('ocean_collection_rename', { from: 'profile' });
    patchCollection(renaming.id, { name: n });
    setRenaming(null);
    flash('Collection renamed.');
  };
  const remove = async (c: Collection) => {
    setDeleting(null);
    const r = await oc.deleteCollection(c.id);
    if (r.ok) {
      ocTrack('ocean_collection_delete', { moved: r.data?.moved ?? 0, shared: !!c.share_token });
      if (openId === c.id) setOpenId(null);
      flash(c.count ? `“${c.name}” deleted. Its ${cards(c.count)} moved to ${dfltName}.` : `“${c.name}” deleted.`);
    } else flash(r.error);
    load();
  };
  const unshare = async (c: Collection) => {
    const r = await oc.unshareCollection(c.id);
    if (r.ok) {
      ocTrack('ocean_collection_unshare', {});
      patchCollection(c.id, { share_token: null });
      flash('Sharing stopped. The link no longer opens this collection.');
    } else flash(r.error);
  };
  const menuFor = (c: Collection, fromTile: boolean): MenuActions => ({
    open: fromTile ? () => openCollection(c) : undefined,
    rename: () => { if (fromTile) setOpenId(c.id); setTimeout(() => setRenaming({ id: c.id, name: c.name, err: '' }), 0); },
    share: () => { if (fromTile) openCollection(c); setShareNow(c.id); },
    unshare: () => unshare(c),
    remove: () => setDeleting(c),
  });

  // ---------------------------------------------------------------- card actions
  const removeCard = async (i: SavedItem) => {
    setItems(xs => (xs || []).filter(x => !(x.kind === i.kind && x.id === i.id)));
    const r = await oc.unsave(i.kind, i.id);
    setToast(r.ok ? { msg: `Removed from ${r.data?.collection || 'your saved cards'}.`,
                      action: { label: 'Undo', run: async () => { await oc.save(i.kind, i.id, i.collection_id); load(); } } }
                  : { msg: r.error });
    ocTrack('ocean_unsave', { kind: i.kind, from: 'profile' });
    load();
  };
  const moveCard = async (i: SavedItem, cid: number) => {
    setMoving(null);
    if (cid === i.collection_id) return;
    const r = await oc.save(i.kind, i.id, cid);
    if (r.ok) { rememberCollection(cid); ocTrack('ocean_move', { kind: i.kind, from: 'profile' }); }
    flash(r.ok ? `Moved to ${r.data?.collection || 'the collection'}.` : r.error);
    load();
  };
  const openCard = (i: SavedItem) => {
    ocTrack('ocean_open', { kind: i.kind, from: 'profile' });
    if (i.kind === 'solution') onOpenTech(i.id);
    else onOpen(i.kind, i.id);
  };

  // ---------------------------------------------------------------- render
  const inCol = current ? all.filter(i => i.collection_id === current.id) : [];
  const fam = (f: Family) => inCol.filter(i => f === 'all' || i.kind === f);
  const shown = fam(family);

  const list = (
    <>
      <div className="occ-head">
        <h3 className="occ-h">Collections</h3>
        {!creating && (
          <button type="button" className="oc-manage" onClick={() => setCreating({ name: '', err: '', busy: false })}>
            <Icon name="add" size={14} />New collection
          </button>
        )}
      </div>
      {creating && (
        <form className="occ-new" onSubmit={create}>
          <input className="oc-input" value={creating.name} maxLength={60} autoFocus placeholder="Collection name"
                 aria-label="New collection name" onChange={e => setCreating({ ...creating, name: e.target.value, err: '' })}
                 onKeyDown={e => { if (e.key === 'Escape') setCreating(null); }} />
          <button type="submit" className="oc-btn oc-btn-sm" disabled={!creating.name.trim() || creating.busy}>Create</button>
          <button type="button" className="oc-btn oc-btn-sm oc-btn-ghost" onClick={() => setCreating(null)}>Cancel</button>
          {creating.err && <div className="occ-err" role="alert" style={{ flexBasis: '100%' }}>{creating.err}</div>}
        </form>
      )}
      <div className="occ-grid">
        {collections.map(c => (
          <div key={c.id} className="occ-tile">
            <button type="button" className="occ-tile-open" onClick={() => openCollection(c)} aria-label={`Open ${c.name}, ${cards(c.count)}`}>
              <span className="occ-tile-icon"><Icon name="bookmark" size={20} /></span>
              <span style={{ minWidth: 0 }}>
                <span className="occ-tile-name" style={{ display: 'block' }}>{c.name}</span>
                <span className="occ-sub">
                  {cards(c.count)}
                  {c.share_token && <span className="occ-flag">Shared</span>}
                </span>
              </span>
            </button>
            <CollectionMenu c={c} a={menuFor(c, true)} className="occ-more" />
          </div>
        ))}
      </div>
      {!all.length && !error && (
        <div className="occ-empty">No saved OCEAN cards yet. Save a problem or company card in OCEAN and it appears here.</div>
      )}
    </>
  );

  const detail = current && (
    <>
      <button type="button" className="occ-back" onClick={() => setOpenId(null)}>
        <Icon name="chevronLeft" size={14} />All collections
      </button>
      <div className="occ-detail-head">
        {renaming?.id === current.id ? (
          <div className="occ-rename">
            <input className="oc-input" value={renaming.name} maxLength={60} autoFocus aria-label={`Rename ${current.name}`}
                   onChange={e => setRenaming({ ...renaming, name: e.target.value, err: '' })}
                   onKeyDown={e => { if (e.key === 'Enter') rename(); if (e.key === 'Escape') setRenaming(null); }} />
            <button type="button" className="oc-btn oc-btn-sm" onClick={rename} disabled={!renaming.name.trim()}>Save</button>
            <button type="button" className="oc-btn oc-btn-sm oc-btn-ghost" onClick={() => setRenaming(null)}>Cancel</button>
            {renaming.err && <div className="occ-err" role="alert" style={{ flexBasis: '100%' }}>{renaming.err}</div>}
          </div>
        ) : (
          <div style={{ minWidth: 0 }}>
            <h3 className="occ-title">{current.name}</h3>
            <div className="occ-sub">
              {cards(current.count)}
              {current.is_default && <span className="occ-flag muted">Default</span>}
              {current.share_token && <span className="occ-flag">Shared</span>}
            </div>
          </div>
        )}
        <div className="occ-actions">
          {COLLECTION_SHARING_ENABLED && (
            <ShareCollection key={`${current.id}:${shareNow === current.id ? 'now' : ''}`} c={current} autoStart={shareNow === current.id}
                             toast={flash} onShared={tok => { patchCollection(current.id, { share_token: tok }); }} />
          )}
          <CollectionMenu c={current} a={menuFor(current, false)} />
        </div>
      </div>
      {inCol.length > 0 && (
        <div className="oc-segpills" role="group" aria-label="Show" style={{ marginTop: 16 }}>
          {([['all', 'All'], ['problem', 'Problem cards'], ['company', 'Company cards']] as [Family, string][]).map(([k, label]) => (
            <button key={k} type="button" aria-pressed={family === k} onClick={() => setFamily(k)}>
              {label} ({fmt(fam(k).length)})
            </button>
          ))}
        </div>
      )}
      {shown.length ? (
        <div className="oc-sgrid">
          {shown.map(i => (
            <div key={`${i.kind}:${i.id}`} className="oc-scard" data-gone={!i.available}>
              <span className="oc-badge" style={{ fontSize: 11, padding: '4px 9px', width: 'fit-content' }}>{BADGE[i.kind]}</span>
              {i.available
                ? <button type="button" className="oc-stitle" onClick={() => openCard(i)}>{i.title}</button>
                : <div className="oc-stitle">{i.title}</div>}
              <div className="oc-cline" style={{ marginTop: 0 }}>{i.available ? i.meta : 'No longer available in Ocean'}</div>
              <div className="oc-row" style={{ gap: 6, marginTop: 'auto', justifyContent: 'flex-end' }}>
                {i.available && collections.length > 1 && (
                  <button type="button" className="oc-link" style={{ fontSize: 12, marginRight: 4 }} title="Move to another collection"
                          aria-label={`Move “${i.title}” to another collection`} onClick={() => setMoving(i)}>Move</button>
                )}
                <button type="button" className="oc-ico" title="Remove from saved" aria-label={`Remove “${i.title}” from saved`}
                        onClick={() => removeCard(i)}><Icon name="trash" size={15} /></button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="occ-empty">
          {inCol.length ? 'No cards of this kind here.' : 'No cards in this collection yet. Save a problem or company card in OCEAN and choose this collection.'}
        </div>
      )}
    </>
  );

  return (
    <div className="ocx ocx-inline">
      {items == null ? <Skeleton rows={3} /> : (
        <>
          {error && <div className="oc-error" role="alert" style={{ marginBottom: 16 }}>{error}
            <button type="button" className="oc-link" onClick={load}>Try again</button></div>}
          {current ? detail : list}
        </>
      )}
      {moving && (
        <CollectionSheet target={{ kind: moving.kind, id: moving.id }} collections={collections} currentId={moving.collection_id}
                         onPick={cid => moveCard(moving, cid)}
                         onCreate={async name => {
                           const r = await oc.createCollection(name);
                           if (!r.ok || !r.data) return r.error;
                           ocTrack('ocean_collection_create', { from: 'move' });
                           await moveCard(moving, r.data.collection.id);
                           return '';
                         }}
                         onRename={async () => ''} onDelete={() => undefined}
                         onRemove={() => { const i = moving; setMoving(null); removeCard(i); }}
                         onClose={() => setMoving(null)} />
      )}
      {deleting && (
        <ConfirmSheet title={`Delete “${deleting.name}”?`} confirmLabel="Delete collection" danger
                      body={<>
                        {deleting.count ? `Its ${cards(deleting.count)} move to ${dfltName}.` : 'It has no cards.'}
                        {deleting.share_token ? ' Its share link stops working.' : ''}
                      </>}
                      onConfirm={() => remove(deleting)} onClose={() => setDeleting(null)} />
      )}
      <Toast t={toast} onDismiss={dismiss} />
    </div>
  );
};

// ---------------------------------------------------------------- a shared collection, in OCEAN
export const SharedCollectionView: React.FC<{ token: string; onClose: () => void }> = ({ token, onClose }) => {
  useCollectionStyles();
  const o = useOcean();
  const [data, setData] = useState<SharedCollection | null>(null);
  const [error, setError] = useState('');
  const [nonce, setNonce] = useState(0);
  useEffect(() => {
    let live = true;
    setData(null); setError('');
    oc.sharedCollection(token).then(r => {
      if (!live) return;
      if (r.ok && r.data) setData(r.data);
      else setError(r.status === 404 ? 'This collection is no longer available.' : (r.error || 'Could not open this collection.'));
    });
    return () => { live = false; };
  }, [token, nonce]);
  const col = data?.collection;
  const by = col ? (col.is_owner ? 'Your collection' : col.owner_first_name ? `Shared by ${col.owner_first_name}` : 'Shared with you') : '';
  return (
    <div className="oc-overlay" onClick={onClose}>
      <div className="oc-base occ-shared" role="dialog" aria-modal="true" aria-label={col ? `Collection ${col.name}` : 'Shared collection'}
           onClick={e => e.stopPropagation()}>
        <div className="oc-bar">
          <span className="oc-badge">Ocean collection</span>
          <button type="button" className="oc-close" title="Close" aria-label="Close" onClick={onClose}><Icon name="close" size={16} /></button>
        </div>
        <div className="oc-body">
          {error ? (
            <div className="occ-empty" role="alert">
              {error}
              {error !== 'This collection is no longer available.' && (
                <div><button type="button" className="oc-link" style={{ marginTop: 10 }} onClick={() => setNonce(n => n + 1)}>Try again</button></div>
              )}
            </div>
          ) : !data || !col ? (
            <div className="oc-loading" aria-busy="true" aria-label="Loading">
              <i style={{ width: '50%', height: 24 }} /><i style={{ width: '30%' }} /><i style={{ width: '70%' }} /><i style={{ width: '60%' }} />
            </div>
          ) : (
            <>
              <h2 className="oc-big">{col.name}</h2>
              <div className="occ-sub" style={{ fontSize: 13 }}>{by} · {cards(col.count)}</div>
              {data.items.length ? (
                <div className="oc-sgrid">
                  {data.items.map(i => {
                    const saved = o.savedOf(i.kind, i.id);
                    const open = () => (i.kind === 'problem'
                      ? o.openProblem(i.id, 'shared_collection')
                      : o.openCompanyBase(i.id, undefined, 'shared_collection'));
                    return (
                      <div key={`${i.kind}:${i.id}`} className="oc-scard">
                        <div className="occ-scard-top">
                          <span className="oc-badge" style={{ fontSize: 11, padding: '4px 9px', width: 'fit-content' }}>{BADGE[i.kind]}</span>
                          <SaveIcon kind={i.kind} saved={!!saved} collection={saved?.collection}
                                    onClick={() => o.toggleSave(i.kind, i.id)} />
                        </div>
                        <button type="button" className="oc-stitle" onClick={open}>{i.title}</button>
                        <div className="oc-cline" style={{ marginTop: 0 }}>{i.meta}</div>
                      </div>
                    );
                  })}
                </div>
              ) : <div className="occ-empty">This collection has no cards yet.</div>}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default OceanSavedTab;
